<template>
  <div>
    <div class="panel-heading pallets-head">
      <h4 class="panel-title"><i class="fa fa-plane"></i> Pallets</h4>
      <button
        type="button"
        class="btn btn-primary"
        @click="openForm()"
      ><i class="fa fa-plus"></i> New pallet</button>
    </div>

    <div
      class="panel-body"
      style="display: block;"
    >
      <div class="summary">
        <div class="summary-tile">
          <small>Pallets</small>
          <span>{{ pallets.length }}</span>
          <em>{{ openCount }} not completed</em>
        </div>
        <div class="summary-tile">
          <small>Orders</small>
          <span>{{ sum('orders_count') }}</span>
          <em :class="sum('waiting_invoice_count') > 0 ? 'warn' : ''">{{ sum('waiting_invoice_count') }} not weighed yet</em>
        </div>
        <div class="summary-tile">
          <small>Income</small>
          <span>{{ sum('income') | usdFormat }}</span>
          <em>from customers</em>
        </div>
        <div class="summary-tile">
          <small>Expenses</small>
          <span>{{ sum('expense') | usdFormat }}</span>
          <em>entered so far</em>
        </div>
        <div
          class="summary-tile summary-profit"
          :class="sum('profit') < 0 ? 'summary-loss' : ''"
        >
          <small>Profit</small>
          <span>{{ sum('profit') | usdFormat }}</span>
          <em>income − expenses</em>
        </div>
      </div>

      <div class="filters">
        <div class="filter filter-wide">
          <label>Search</label>
          <input
            type="text"
            class="form-control"
            placeholder="Pallet / plane number"
            v-model="queryParams.search"
            @keyup.enter="getData()"
          />
        </div>
        <div class="filter">
          <label>Flight date from</label>
          <input
            type="date"
            class="form-control"
            v-model="queryParams.from_date"
          />
        </div>
        <div class="filter">
          <label>Flight date to</label>
          <input
            type="date"
            class="form-control"
            v-model="queryParams.to_date"
          />
        </div>
        <div class="filter">
          <label>Origin</label>
          <select
            class="form-control"
            v-model="queryParams.warehouse_id"
          >
            <option value="">All origins</option>
            <option
              v-for="w in warehouses"
              :key="w.warehouse_id"
              :value="w.warehouse_id"
            >{{ w.country }}</option>
          </select>
        </div>
        <div class="filter">
          <label>Accounts</label>
          <select
            class="form-control"
            v-model="queryParams.is_completed"
          >
            <option value="">All accounts</option>
            <option value="0">Not completed</option>
            <option value="1">Completed</option>
          </select>
        </div>
        <div class="filter filter-buttons">
          <button
            type="button"
            class="btn btn-primary"
            @click="getData()"
          ><i class="fa fa-search"></i> Filter</button>
          <button
            type="button"
            class="btn btn-default"
            @click="resetFilter()"
          >Reset</button>
        </div>
      </div>

      <div
        v-if="pallets.length == 0"
        class="empty-state"
      >
        <i class="fa fa-plane"></i>
        <h4>No pallets here</h4>
        <p>A pallet is made on the Pending To Shipment page when orders are sent, or with "New pallet".</p>
      </div>

      <div
        v-else
        class="table-responsive"
      >
        <table class="table pallets-table">
          <thead>
            <tr>
              <th>Pallet / Plane No</th>
              <th>Accounts</th>
              <th>Orders</th>
              <th title="Ship Only and pay-by-weight orders that have no Shipping Invoice yet">Not weighed yet</th>
              <th class="money">Income</th>
              <th class="money">Expenses</th>
              <th class="money">Profit</th>
              <th>Note</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="p in pallets"
              :key="p.pallet_id"
              @click="showPallet(p)"
            >
              <td>
                <div class="pallet-no"><i class="fa fa-plane"></i> {{ p.tracking_number }} <span class="origin">{{ p.origin }}</span></div>
                <small v-if="p.flight_date">Flight {{ p.flight_date }}</small>
                <small v-else>No flight date</small>
                <small
                  v-if="p.is_closed"
                  class="closed"
                >Flew, takes no new orders</small>
                <small
                  v-else
                  class="loading"
                >Loading at the warehouse</small>
              </td>
              <td>
                <span
                  class="pill"
                  :class="p.is_completed ? 'pill-done' : 'pill-open'"
                >{{ p.is_completed ? 'Completed' : 'Not completed' }}</span>
                <small
                  v-for="reason in shortReasons(p)"
                  :key="reason"
                >{{ reason }}</small>
              </td>
              <td>
                <strong>{{ p.orders_count }}</strong>
                <small>{{ p.arrived_count }} arrived · {{ p.delivered_count }} delivered</small>
              </td>
              <td>
                <span
                  v-if="p.waiting_invoice_count > 0"
                  class="pill pill-wait"
                >{{ p.waiting_invoice_count }}</span>
                <span
                  v-else
                  class="none"
                >—</span>
              </td>
              <td class="money">{{ p.income | usdFormat }}</td>
              <td class="money">{{ p.expense | usdFormat }}</td>
              <td
                class="money profit"
                :class="p.profit < 0 ? 'loss' : ''"
              >{{ p.profit | usdFormat }}</td>
              <td class="note">{{ p.note }}</td>
              <td
                class="actions"
                @click.stop
              >
                <a
                  title="Report"
                  @click="showPallet(p)"
                ><i class="fa fa-bar-chart"></i></a>
                <a
                  title="Edit"
                  @click="openForm(p)"
                ><i class="fa fa-pencil"></i></a>
                <a
                  v-if="p.orders_count == 0 && p.items_count == 0"
                  title="Delete"
                  class="danger"
                  @click="deletePallet(p)"
                ><i class="fa fa-trash"></i></a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="legend">
        <i class="fa fa-info-circle"></i>
        <div>
          <strong>Income</strong> = ShipZone fee + Int Shipping + Local Tax paid on the order + Shipping Invoice (shipment + handling + tax − discount).
          <strong>Profit</strong> = Income − Expenses.
          A pallet becomes <strong>Completed</strong> by itself when every Ship Only and pay-by-weight order is weighed in Erbil
          and the Turkish Airlines invoice, airport expenses and airport tax are entered. Until then the profit is provisional.
          A pallet is one plane from one origin; once it has flown it takes no more orders from the warehouse.
        </div>
      </div>
    </div>

    <v-dialog
      v-model="formModal"
      persistent
      max-width="450px"
    >
      <v-card>
        <form @submit.prevent="savePallet()">
          <v-card-title>
            <span class="headline">{{ formData.pallet_id ? 'Edit pallet' : 'New pallet' }}</span>
          </v-card-title>
          <v-card-text>
            <div class="form-group">
              <label>Origin (where the plane comes from)*</label>
              <select
                class="form-control"
                v-model="formData.warehouse_id_fk"
                required
              >
                <option :value="null">Choose origin</option>
                <option
                  v-for="w in warehouses"
                  :key="w.warehouse_id"
                  :value="w.warehouse_id"
                >{{ w.country }}</option>
              </select>
            </div>
            <div class="form-group">
              <label>Pallet / Plane tracking number*</label>
              <input
                type="text"
                class="form-control"
                v-model="formData.tracking_number"
                required
              />
            </div>
            <div class="form-group">
              <label>Flight date</label>
              <input
                type="date"
                class="form-control"
                v-model="formData.flight_date"
              />
            </div>
            <div class="form-group">
              <label>Note</label>
              <input
                type="text"
                class="form-control"
                v-model="formData.note"
              />
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              style="height:auto"
              outline
              @click="formModal = false"
            >Cancel</v-btn>
            <v-btn
              type="submit"
              style="height:auto"
              outline
              color="success"
            >Save</v-btn>
          </v-card-actions>
        </form>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="palletModal"
      max-width="1250px"
      scrollable
    >
      <v-card v-if="pallet">
        <v-card-title>
          <span class="headline">
            <i class="fa fa-plane"></i> {{ pallet.tracking_number }} <span class="origin">{{ pallet.origin }}</span>
          </span>
          <small
            v-if="pallet.flight_date"
            style="margin-left: 12px; color: #777"
          >Flight date: {{ pallet.flight_date }}</small>
          <v-spacer></v-spacer>
          <span
            class="pill pill-big"
            :class="pallet.is_completed ? 'pill-done' : 'pill-open'"
          ><i
              class="fa"
              :class="pallet.is_completed ? 'fa-check-circle' : 'fa-clock-o'"
            ></i> {{ pallet.is_completed ? 'Accounts completed' : 'Accounts not completed' }}</span>
        </v-card-title>
        <v-card-text class="report-body">
          <div class="row text-center report-tiles">
            <div class="col-sm-3">
              <h5>Orders</h5>
              <h3>{{ pallet.orders_count + pallet.items_count }}</h3>
              <small
                v-if="pallet.waiting_invoice_count > 0"
                class="warn"
              >{{ pallet.waiting_invoice_count }} not weighed yet</small>
            </div>
            <div class="col-sm-3">
              <h5>Income</h5>
              <h3>{{ pallet.income | usdFormat }}</h3>
            </div>
            <div class="col-sm-3">
              <h5>Expenses</h5>
              <h3>{{ pallet.expense | usdFormat }}</h3>
              <small
                v-if="(pallet.missing_expenses || []).length > 0"
                class="warn"
              >{{ pallet.missing_expenses.length }} not entered yet</small>
            </div>
            <div class="col-sm-3">
              <h5>{{ pallet.is_completed ? 'Final profit' : 'Provisional profit' }}</h5>
              <h3 :style="profitStyle(pallet.profit)">{{ pallet.profit | usdFormat }}</h3>
            </div>
          </div>

          <div
            v-if="pallet.is_completed"
            class="status-box status-done"
          >
            <strong><i class="fa fa-check-circle"></i> Completed.</strong>
            Every order is weighed and every expense is entered, so this profit is final.
          </div>
          <div
            v-else
            class="status-box status-open"
          >
            <strong>Not completed yet.</strong> Still needed:
            <ul>
              <li
                v-for="reason in statusReasons(pallet)"
                :key="reason"
              >{{ reason }}</li>
            </ul>
          </div>

          <div class="row report-money">
            <div class="col-sm-6">
              <h5 class="section-title">Income</h5>
              <table class="table table-condensed income-table">
                <tbody>
                  <tr>
                    <td>
                      From orders
                      <small>
                        ShipZone fee {{ pallet.shipzone_fee | usdFormat }} &middot;
                        Int Shipping {{ pallet.int_shipping | usdFormat }} &middot;
                        Local Tax {{ pallet.local_tax | usdFormat }}
                      </small>
                    </td>
                    <td class="text-right">{{ pallet.on_order | usdFormat }}</td>
                  </tr>
                  <tr>
                    <td>
                      From Shipping Invoices
                      <small>
                        Shipment {{ pallet.invoice_shipment | usdFormat }} &middot;
                        Handling fee {{ pallet.invoice_handling | usdFormat }} &middot;
                        Tax {{ pallet.invoice_tax | usdFormat }}
                        <template v-if="pallet.invoice_discount > 0">
                          &middot; Discount &minus;{{ pallet.invoice_discount | usdFormat }}
                        </template>
                      </small>
                    </td>
                    <td class="text-right">{{ pallet.on_invoice | usdFormat }}</td>
                  </tr>
                  <tr v-if="pallet.items_count > 0">
                    <td>
                      Added by hand
                      <small>
                        {{ pallet.items_count }} {{ pallet.items_count == 1 ? 'item' : 'items' }} not in the system &middot;
                        ShipZone fee {{ pallet.by_hand_shipzone_fee | usdFormat }} &middot;
                        Int Shipping {{ pallet.by_hand_int_shipping | usdFormat }} &middot;
                        Local Tax {{ pallet.by_hand_local_tax | usdFormat }} &middot;
                        Handling fee {{ pallet.by_hand_handling_fee | usdFormat }}
                      </small>
                    </td>
                    <td class="text-right">{{ pallet.by_hand | usdFormat }}</td>
                  </tr>
                  <tr class="income-total">
                    <td>Total income</td>
                    <td class="text-right">{{ pallet.income | usdFormat }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="col-sm-6">
              <h5 class="section-title section-bar">
                Expenses
                <button
                  type="button"
                  class="btn btn-default btn-xs"
                  @click="openExpenseForm('')"
                ><i class="fa fa-plus"></i> Add expense</button>
              </h5>
              <table class="table table-condensed income-table">
                <tbody>
                  <tr
                    v-for="e in expenses"
                    :key="e.pallet_expense_id"
                  >
                    <td>
                      {{ e.name }}
                      <small v-if="e.expense_date || e.note">{{ [e.expense_date, e.note].filter(x => x).join(' · ') }}</small>
                    </td>
                    <td class="text-right">
                      {{ e.amount | usdFormat }}
                      <a
                        class="row-action"
                        title="Delete"
                        @click="deleteExpense(e)"
                      ><i class="fa fa-trash"></i></a>
                    </td>
                  </tr>
                  <tr
                    v-for="name in (pallet.missing_expenses || [])"
                    :key="'missing-' + name"
                    class="pending-row"
                    title="Click to enter it"
                    @click="openExpenseForm(name)"
                  >
                    <td>{{ name }}</td>
                    <td class="text-right"><span class="pill pill-open">Not entered</span></td>
                  </tr>
                  <tr class="income-total">
                    <td>Total expenses</td>
                    <td class="text-right">{{ pallet.expense | usdFormat }}</td>
                  </tr>
                </tbody>
              </table>
              <form
                v-if="expenseFormOpen"
                class="add-box form-inline"
                @submit.prevent="addExpense()"
              >
                <select
                  class="form-control"
                  v-model="expenseForm.name"
                  required
                >
                  <option value="">Expense type</option>
                  <option
                    v-for="n in expenseNames"
                    :key="n"
                    :value="n"
                  >{{ n }}</option>
                </select>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  class="form-control"
                  style="width: 110px"
                  placeholder="Amount $"
                  v-model.number="expenseForm.amount"
                  required
                />
                <input
                  type="date"
                  class="form-control"
                  v-model="expenseForm.expense_date"
                />
                <input
                  type="text"
                  class="form-control"
                  style="width: 130px"
                  placeholder="Note"
                  v-model="expenseForm.note"
                />
                <button
                  type="submit"
                  class="btn btn-primary"
                ><i class="fa fa-plus"></i> Add</button>
                <small class="add-help">If the pallet has none of this expense, enter 0.</small>
              </form>
            </div>
          </div>

          <h5 class="section-title section-bar">
            Orders
            <span>
              <button
                type="button"
                class="btn btn-default btn-xs"
                @click="addOrdersOpen = !addOrdersOpen; itemFormOpen = false"
              ><i class="fa fa-plus"></i> Add orders</button>
              <button
                type="button"
                class="btn btn-default btn-xs"
                style="margin-left: 6px"
                @click="itemFormOpen = !itemFormOpen; addOrdersOpen = false"
              ><i class="fa fa-plus"></i> Add item not in the system</button>
            </span>
          </h5>
          <form
            v-if="addOrdersOpen"
            class="add-box"
            @submit.prevent="addOrders(false)"
          >
            <label>Orders that flew on this pallet but are not listed here. Write the order numbers (US-72911) or scan their QR codes, one per line or with commas.</label>
            <textarea
              class="form-control"
              rows="3"
              placeholder="US-72911, US-72926"
              v-model="addOrdersText"
              required
            ></textarea>
            <button
              type="submit"
              class="btn btn-primary"
            ><i class="fa fa-plus"></i> Add to this pallet</button>
          </form>
          <form
            v-if="itemFormOpen"
            class="add-box"
            @submit.prevent="addItem()"
          >
            <label>
              Something on this pallet that is not an order in the system. Fill the lines like an order.
              The item price is kept for the record only; it is not counted in the income, same as for orders.
            </label>
            <div class="item-fields">
              <div class="item-field item-wide">
                <small>Item name*</small>
                <input
                  type="text"
                  class="form-control"
                  v-model="itemForm.name"
                  required
                />
              </div>
              <div class="item-field item-wide">
                <small>Customer</small>
                <input
                  type="text"
                  class="form-control"
                  v-model="itemForm.customer_name"
                />
              </div>
              <div class="item-field">
                <small>Item price $ (not counted)</small>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  class="form-control"
                  placeholder="0.00"
                  v-model.number="itemForm.item_price"
                />
              </div>
              <div
                v-for="line in itemLines"
                :key="line.key"
                class="item-field"
              >
                <small>{{ line.label }} $</small>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  class="form-control"
                  placeholder="0.00"
                  v-model.number="itemForm[line.key]"
                />
              </div>
              <div class="item-field item-wide">
                <small>Note</small>
                <input
                  type="text"
                  class="form-control"
                  v-model="itemForm.note"
                />
              </div>
            </div>
            <div class="item-total">
              Income of this item: <strong>{{ itemFormTotal | usdFormat }}</strong>
              <button
                type="submit"
                class="btn btn-primary"
              ><i class="fa fa-plus"></i> Add item</button>
            </div>
          </form>
          <div class="table-responsive orders-scroll">
            <table class="table table-bordered pallet-orders">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Customer</th>
                  <th>Stage</th>
                  <th
                    class="text-right"
                    title="ShipZone fee + Int Shipping + Local Tax paid on the order"
                  >On order</th>
                  <th>Shipping Invoice</th>
                  <th class="text-right">Income</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!loaded">
                  <td
                    colspan="7"
                    class="text-center"
                    style="color: #999"
                  >Loading…</td>
                </tr>
                <tr v-else-if="orders.length == 0 && items.length == 0">
                  <td
                    colspan="7"
                    class="text-center"
                    style="color: #999"
                  >No orders on this pallet yet. They are put on it when sent from Pending To Shipment.</td>
                </tr>
                <tr
                  v-for="o in sortedOrders"
                  :key="o.order_id"
                >
                  <td style="white-space: nowrap">
                    {{ o.theOrderID }}
                    <small
                      v-if="o.is_ship_only"
                      style="margin-left: 6px; color: #8e44ad"
                    >Ship only</small>
                  </td>
                  <td>{{ o.full_name }}</td>
                  <td>{{ stageName(o.status) }}</td>
                  <td
                    class="text-right"
                    :title="onOrderParts(o)"
                  >{{ o.on_order | usdFormat }}</td>
                  <td style="white-space: nowrap">
                    <span v-if="o.invoice_number">
                      {{ o.invoice_number }}
                      <span class="invoice-money">{{ o.on_invoice | usdFormat }}</span>
                    </span>
                    <span
                      v-else-if="o.waiting_invoice"
                      style="color: #e67e22; font-weight: bold"
                    >Not weighed yet</span>
                    <span
                      v-else-if="o.pays_on_invoice"
                      style="color: #999"
                    >No invoice</span>
                    <span
                      v-else
                      class="none"
                    >&ndash;</span>
                  </td>
                  <td class="text-right"><strong>{{ o.income | usdFormat }}</strong></td>
                  <td class="text-center">
                    <a
                      class="row-action"
                      title="Move to another pallet"
                      @click="openMove(o)"
                    ><i class="fa fa-exchange"></i></a>
                  </td>
                </tr>
                <tr
                  v-for="i in items"
                  :key="'item-' + i.pallet_item_id"
                  class="hand-row"
                >
                  <td>
                    {{ i.name }}
                    <small style="margin-left: 6px; color: #8e44ad">By hand</small>
                    <small
                      v-if="i.item_price > 0"
                      style="display: block; color: #999"
                    >Item price {{ i.item_price | usdFormat }} (not counted)</small>
                  </td>
                  <td>{{ i.customer_name }}</td>
                  <td>Not in the system</td>
                  <td class="text-right none">&ndash;</td>
                  <td style="color: #999">{{ i.note }}</td>
                  <td
                    class="text-right"
                    :title="itemParts(i)"
                  ><strong>{{ i.amount | usdFormat }}</strong></td>
                  <td class="text-center">
                    <a
                      class="row-action"
                      title="Delete"
                      @click="deleteItem(i)"
                    ><i class="fa fa-trash"></i></a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            style="height:auto"
            outline
            @click="palletModal = false"
          >Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="moveModal"
      persistent
      max-width="450px"
    >
      <v-card v-if="moveOrder">
        <form @submit.prevent="moveSubmit()">
          <v-card-title>
            <span class="headline">Move {{ moveOrder.theOrderID }}</span>
          </v-card-title>
          <v-card-text>
            <div class="form-group">
              <label>Move it to pallet*</label>
              <select
                class="form-control"
                v-model="moveTo"
                required
              >
                <option :value="null">Choose pallet</option>
                <option
                  v-for="p in otherPallets"
                  :key="p.pallet_id"
                  :value="p.pallet_id"
                >{{ p.tracking_number }}{{ p.flight_date ? ' (' + p.flight_date + ')' : '' }}</option>
              </select>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              style="height:auto"
              outline
              @click="moveModal = false"
            >Cancel</v-btn>
            <v-btn
              type="submit"
              style="height:auto"
              outline
              color="success"
            >Move</v-btn>
          </v-card-actions>
        </form>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import swal from 'sweetalert'

export default {
  data() {
    return {
      pallets: [],
      queryParams: {
        search: '',
        from_date: '',
        to_date: '',
        warehouse_id: '',
        is_completed: ''
      },
      warehouses: [],
      formModal: false,
      formData: {
        pallet_id: null,
        tracking_number: '',
        warehouse_id_fk: null,
        flight_date: '',
        note: ''
      },
      palletModal: false,
      pallet: null,
      expenses: [],
      orders: [],
      items: [],
      loaded: false,
      itemForm: { name: '', customer_name: '', item_price: '', shipzone_fee: '', int_shipping: '', local_tax: '', handling_fee: '', note: '' },
      itemLines: [
        { key: 'shipzone_fee', label: 'ShipZone fee' },
        { key: 'int_shipping', label: 'Int Shipping' },
        { key: 'local_tax', label: 'Local Tax' },
        { key: 'handling_fee', label: 'Handling fee' }
      ],
      itemFormOpen: false,
      expenseFormOpen: false,
      addOrdersOpen: false,
      addOrdersText: '',
      moveModal: false,
      moveOrder: null,
      moveTo: null,
      pickerPallets: [],
      expenseNames: ['Turkish Airlines invoice', 'Airport expenses', 'Airport tax', 'Other'],
      stageNames: {
        purchased: 'Purchased',
        warehouse_delivered_order: 'Warehouse Delivered',
        pending_to_shipment: 'Pending To Shipment',
        int_shipment: 'International Shipment',
        pending_to_branch: 'Pending To Branch',
        branch_delivery: 'Delivered to Erbil',
        delivered: 'Delivered',
        archived: 'Archived',
        refunded: 'Refunded',
        canceled: 'Canceled'
      },
      expenseForm: {
        name: '',
        amount: '',
        expense_date: '',
        note: ''
      }
    }
  },

  computed: {
    // orders still waiting to be weighed in Erbil come first
    openCount() {
      return this.pallets.filter(p => !p.is_completed).length
    },

    itemFormTotal() {
      return this.itemLines.reduce((total, line) => total + (Number(this.itemForm[line.key]) || 0), 0)
    },

    otherPallets() {
      // an order can only move to another plane from the same origin
      return this.pickerPallets.filter(p => this.pallet && p.pallet_id != this.pallet.pallet_id && p.warehouse_id_fk == this.pallet.warehouse_id_fk)
    },

    sortedOrders() {
      return this.orders.slice().sort((a, b) => (b.waiting_invoice ? 1 : 0) - (a.waiting_invoice ? 1 : 0))
    }
  },

  mounted() {
    // a pallet card on the home page links here with ?pallet=ID and opens that report
    this.getData(this.$route.query.pallet)
    axios
      .get('api/warehouses', { headers: this.headers() })
      .then(response => { this.warehouses = response.data.data || [] })
      .catch(() => { })
  },

  methods: {
    headers() {
      return { Authorization: 'Bearer ' + localStorage.getItem('access_token') }
    },

    getData(openPalletId) {
      axios
        .get('api/pallets', { params: this.queryParams, headers: this.headers() })
        .then(response => {
          this.pallets = response.data
          const toOpen = this.pallets.find(p => String(p.pallet_id) === String(openPalletId))
          if (toOpen) {
            this.showPallet(toOpen)
          }
        })
        .catch(error => this.showError(error))
    },

    resetFilter() {
      this.queryParams.search = ''
      this.queryParams.from_date = ''
      this.queryParams.to_date = ''
      this.queryParams.warehouse_id = ''
      this.queryParams.is_completed = ''
      this.getData()
    },

    sum(key) {
      return this.pallets.reduce((total, p) => total + (Number(p[key]) || 0), 0)
    },

    // What a pallet still needs before it is completed (nobody switches that by hand).
    statusReasons(p) {
      const reasons = []
      if (p.orders_count == 0 && p.items_count == 0) {
        reasons.push('Orders are sent on this pallet')
      }
      if (p.waiting_invoice_count > 0) {
        reasons.push(p.waiting_invoice_count + (p.waiting_invoice_count == 1 ? ' order is' : ' orders are') +
          ' weighed in Erbil and get a Shipping Invoice (listed first below; their money is not counted yet)')
      }
      ;(p.missing_expenses || []).forEach(name => reasons.push(name + ' is entered'))
      return reasons
    },

    shortReasons(p) {
      if (p.is_completed) {
        return []
      }
      const reasons = []
      const missing = (p.missing_expenses || []).length
      if (p.orders_count == 0 && p.items_count == 0) {
        reasons.push('no orders yet')
      }
      if (p.waiting_invoice_count > 0) {
        reasons.push(p.waiting_invoice_count + ' not weighed')
      }
      if (missing > 0) {
        reasons.push(missing + (missing == 1 ? ' expense' : ' expenses') + ' missing')
      }
      return reasons
    },

    stageName(status) {
      return this.stageNames[status] || status
    },

    // hover text of the "On order" amount: the three things it is made of
    onOrderParts(o) {
      const money = this.$options.filters.usdFormat
      return 'ShipZone fee ' + money(o.shipzone_fee) + ' + Int Shipping ' + money(o.int_shipping) + ' + Local Tax ' + money(o.local_tax)
    },

    // hover text of a hand item's income: the lines it is made of
    itemParts(item) {
      const money = this.$options.filters.usdFormat
      return this.itemLines.map(line => line.label + ' ' + money(item[line.key] || 0)).join(' + ')
    },

    profitStyle(profit) {
      return 'font-weight: bold; color: ' + (profit < 0 ? '#d9534f' : '#27ae60')
    },

    openForm(pallet) {
      this.formData = {
        pallet_id: pallet ? pallet.pallet_id : null,
        tracking_number: pallet ? pallet.tracking_number : '',
        warehouse_id_fk: pallet ? pallet.warehouse_id_fk : (this.warehouses.length > 0 ? this.warehouses[0].warehouse_id : null),
        flight_date: pallet ? pallet.flight_date : '',
        note: pallet ? pallet.note : ''
      }
      this.formModal = true
    },

    savePallet() {
      const request = this.formData.pallet_id
        ? axios.put('api/pallets/' + this.formData.pallet_id, this.formData, { headers: this.headers() })
        : axios.post('api/pallets', this.formData, { headers: this.headers() })

      request
        .then(() => {
          this.formModal = false
          this.getData()
        })
        .catch(error => this.showError(error))
    },

    deletePallet(pallet) {
      swal({
        title: 'Are you sure?',
        text: 'Delete pallet ' + pallet.tracking_number,
        icon: 'warning',
        buttons: true
      }).then(confirmed => {
        if (confirmed) {
          axios
            .delete('api/pallets/' + pallet.pallet_id, { headers: this.headers() })
            .then(() => this.getData())
            .catch(error => this.showError(error))
        }
      })
    },

    showPallet(pallet) {
      this.pallet = pallet
      this.expenses = []
      this.orders = []
      this.items = []
      this.loaded = false
      this.addOrdersOpen = false
      this.itemFormOpen = false
      this.expenseFormOpen = false
      this.addOrdersText = ''
      this.palletModal = true
      this.loadPallet()
    },

    loadPallet() {
      axios
        .get('api/pallets/' + this.pallet.pallet_id, { headers: this.headers() })
        .then(response => {
          this.pallet = response.data.pallet
          this.expenses = response.data.expenses
          this.orders = response.data.orders
          this.items = response.data.items
          this.loaded = true
        })
        .catch(error => this.showError(error))
    },

    openExpenseForm(name) {
      this.expenseForm.name = name
      this.expenseFormOpen = true
    },

    addExpense() {
      axios
        .post('api/pallets/' + this.pallet.pallet_id + '/expenses', this.expenseForm, { headers: this.headers() })
        .then(() => {
          this.expenseForm = { name: '', amount: '', expense_date: '', note: '' }
          this.expenseFormOpen = false
          this.loadPallet()
          this.getData()
        })
        .catch(error => this.showError(error))
    },

    deleteExpense(expense) {
      swal({
        title: 'Are you sure?',
        text: 'Delete ' + expense.name,
        icon: 'warning',
        buttons: true
      }).then(confirmed => {
        if (confirmed) {
          axios
            .delete('api/pallet_expenses/' + expense.pallet_expense_id, { headers: this.headers() })
            .then(() => {
              this.loadPallet()
              this.getData()
            })
            .catch(error => this.showError(error))
        }
      })
    },

    // Orders already in the system that flew on this pallet without being put on it.
    addOrders(move) {
      const codes = this.addOrdersText.split(/[\s,]+/).filter(code => code)
      axios
        .post('api/pallets/' + this.pallet.pallet_id + '/orders', { codes: codes, move: move }, { headers: this.headers() })
        .then(response => {
          this.addOrdersText = ''
          this.addOrdersOpen = false
          this.loadPallet()
          this.getData()
          swal({ title: 'Done', text: response.data.message, icon: 'success', timer: 1800, buttons: false })
        })
        .catch(error => {
          const onOtherPallet = error.response && error.response.status == 409 && error.response.data.needs_move
          if (!onOtherPallet) {
            return this.showError(error)
          }
          swal({
            title: 'Are you sure?',
            text: 'These are on another pallet and will be moved to this one: ' + onOtherPallet.join(', '),
            icon: 'warning',
            buttons: true
          }).then(confirmed => {
            if (confirmed) {
              this.addOrders(true)
            }
          })
        })
    },

    openMove(order) {
      this.moveOrder = order
      this.moveTo = null
      this.moveModal = true
      axios
        .get('api/pallets/list', { headers: this.headers() })
        .then(response => {
          this.pickerPallets = response.data
        })
        .catch(error => this.showError(error))
    },

    moveSubmit() {
      const target = this.pickerPallets.find(p => p.pallet_id == this.moveTo)
      swal({
        title: 'Are you sure?',
        text: 'Move ' + this.moveOrder.theOrderID + ' to pallet ' + target.tracking_number,
        icon: 'warning',
        buttons: true
      }).then(confirmed => {
        if (!confirmed) {
          return
        }
        axios
          .post('api/pallets/' + target.pallet_id + '/orders', { order_ids: [this.moveOrder.order_id], move: true }, { headers: this.headers() })
          .then(() => {
            this.moveModal = false
            this.loadPallet()
            this.getData()
          })
          .catch(error => this.showError(error))
      })
    },

    addItem() {
      axios
        .post('api/pallets/' + this.pallet.pallet_id + '/items', this.itemForm, { headers: this.headers() })
        .then(() => {
          this.itemForm = { name: '', customer_name: '', item_price: '', shipzone_fee: '', int_shipping: '', local_tax: '', handling_fee: '', note: '' }
          this.itemFormOpen = false
          this.loadPallet()
          this.getData()
        })
        .catch(error => this.showError(error))
    },

    deleteItem(item) {
      swal({
        title: 'Are you sure?',
        text: 'Delete ' + item.name,
        icon: 'warning',
        buttons: true
      }).then(confirmed => {
        if (confirmed) {
          axios
            .delete('api/pallet_items/' + item.pallet_item_id, { headers: this.headers() })
            .then(() => {
              this.loadPallet()
              this.getData()
            })
            .catch(error => this.showError(error))
        }
      })
    },

    showError(error) {
      let message = (error.response && error.response.data.message) || 'Something went wrong'
      if (typeof message !== 'string') {
        // validation errors come as { field: [messages] }
        message = Object.values(message).map(m => [].concat(m).join(' ')).join(' ')
      }
      swal('Error', message, 'error')
    }
  }
}
</script>

<style scoped>
.pallets-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.pallets-head h4 {
  text-transform: uppercase;
  font-weight: 500;
}

/* ---- summary tiles */
.summary {
  display: flex;
  flex-wrap: wrap;
  margin: 0 -8px 18px;
}

.summary-tile {
  flex: 1 1 170px;
  margin: 0 8px 12px;
  padding: 14px 18px;
  background: #f7f8fc;
  border: 1px solid #e6e8f0;
  border-radius: 6px;
}

.summary-tile small {
  display: block;
  color: #7a8194;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-size: 11px;
}

.summary-tile span {
  display: block;
  margin: 4px 0 2px;
  font-size: 24px;
  font-weight: 600;
  color: #2c315b;
}

.summary-tile em {
  font-style: normal;
  font-size: 12px;
  color: #9aa0b0;
}

.summary-tile em.warn {
  color: #e67e22;
}

.summary-profit {
  background: #eaf7ef;
  border-color: #cfead9;
}

.summary-profit span {
  color: #1e8e4e;
}

.summary-loss {
  background: #fdecea;
  border-color: #f5c6c2;
}

.summary-loss span {
  color: #d9534f;
}

/* ---- filters */
.filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  margin-bottom: 18px;
  padding: 14px 16px 4px;
  border: 1px solid #e6e8f0;
  border-radius: 6px;
}

.filter {
  margin: 0 14px 10px 0;
}

.filter-wide {
  flex: 1 1 220px;
}

.filter label {
  display: block;
  margin-bottom: 4px;
  font-size: 12px;
  font-weight: normal;
  color: #7a8194;
}

.filters .form-control,
form.form-inline .form-control {
  height: 36px;
  padding: 6px 10px;
  background: #fff;
  border: 1px solid #d5d9e3;
  border-radius: 4px;
}

.filter-wide .form-control {
  width: 100%;
}

.filter-buttons .btn {
  margin-right: 6px;
}

form.form-inline .form-control {
  margin: 0 6px 6px 0;
}

/* the theme hides the arrow of plain dropdowns; show it so they read as dropdowns */
select.form-control {
  -webkit-appearance: menulist;
  appearance: auto;
}

/* ---- list */
.pallets-table {
  border: 1px solid #e6e8f0;
}

.pallets-table thead th {
  background: #f7f8fc;
  color: #7a8194;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  border-bottom: 1px solid #e6e8f0;
}

.pallets-table tbody tr {
  cursor: pointer;
}

.pallets-table tbody tr:hover {
  background: #f4f6ff;
}

.pallets-table td {
  vertical-align: middle;
}

.pallets-table td small {
  display: block;
  color: #9aa0b0;
  white-space: nowrap;
}

.pallet-no {
  white-space: nowrap;
  font-size: 15px;
  font-weight: 600;
  color: #2c315b;
}

.pallet-no i {
  margin-right: 4px;
  color: #5a67d8;
}

.origin {
  margin-left: 6px;
  padding: 1px 8px;
  border-radius: 10px;
  background: #eef0fb;
  color: #5a67d8;
  font-size: 12px;
  font-weight: 600;
}

.pallets-table td small.closed {
  color: #7a8194;
}

.pallets-table td small.loading {
  color: #1e8e4e;
}

.money {
  text-align: right;
  white-space: nowrap;
}

.profit {
  font-weight: 700;
  color: #1e8e4e;
}

.profit.loss {
  color: #d9534f;
}

.note {
  max-width: 180px;
  color: #777;
}

.none {
  color: #c3c7d1;
}

.pill {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.pill-open {
  background: #fff1e0;
  color: #c96a12;
}

.pill-done {
  background: #e3f6ea;
  color: #1e8e4e;
}

.pill-wait {
  background: #fde8e6;
  color: #d9534f;
}

.actions {
  white-space: nowrap;
  text-align: right;
  cursor: default;
}

.actions a {
  display: inline-block;
  width: 30px;
  height: 30px;
  margin-left: 4px;
  line-height: 30px;
  text-align: center;
  border-radius: 4px;
  color: #5a67d8;
  background: #eef0fb;
}

.actions a:hover {
  background: #dfe3f8;
}

.actions a.danger {
  color: #d9534f;
  background: #fdecea;
}

.empty-state {
  padding: 50px 20px;
  text-align: center;
  color: #9aa0b0;
  border: 1px dashed #d5d9e3;
  border-radius: 6px;
}

.empty-state i {
  font-size: 40px;
  color: #c3c7d1;
}

.empty-state h4 {
  margin: 12px 0 6px;
  color: #555;
}

.legend {
  display: flex;
  margin-top: 16px;
  padding: 10px 14px;
  background: #f7f8fc;
  border-radius: 6px;
  color: #6b7285;
  font-size: 13px;
}

.legend i {
  margin: 2px 10px 0 0;
  color: #5a67d8;
}

/* ---- report box */
/* the report can be long: the body scrolls, the title and the Close button stay in place */
.report-body {
  max-height: calc(100vh - 190px);
  overflow-y: auto;
}

.section-title {
  margin: 18px 0 8px;
  font-weight: 600;
  color: #2c315b;
}

.report-tiles small.warn {
  color: #e67e22;
}

.row-action {
  margin-left: 10px;
  color: #b4b9c6;
  cursor: pointer;
}

.row-action:hover {
  color: #d9534f;
}

.add-box label {
  display: block;
  margin-bottom: 6px;
  font-weight: normal;
  color: #6b7285;
}

.add-help {
  display: block;
  margin-top: 4px;
  color: #9aa0b0;
}

.item-fields {
  display: flex;
  flex-wrap: wrap;
  margin: 0 -5px;
}

.item-field {
  flex: 1 1 120px;
  margin: 0 5px 8px;
}

.item-field.item-wide {
  flex: 2 1 190px;
}

.item-field small {
  display: block;
  margin-bottom: 2px;
  color: #7a8194;
}

.item-field .form-control {
  width: 100%;
  height: 36px;
  padding: 6px 10px;
  background: #fff;
  border: 1px solid #d5d9e3;
  border-radius: 4px;
}

.item-total {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  color: #2c315b;
}

.item-total .btn {
  margin-left: 14px;
}

.hand-row td {
  background: #fbfaff;
}

.report-tiles > div {
  padding-top: 6px;
  padding-bottom: 10px;
}

.report-tiles h5 {
  color: #7a8194;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-size: 12px;
}

.report-tiles h3 {
  margin: 6px 0;
  font-weight: 600;
}

.status-box {
  margin: 14px 0 0;
  padding: 12px 16px;
  border-radius: 4px;
}

.status-box ul {
  margin: 6px 0 0;
  padding-left: 20px;
}

.status-open {
  background: #fff8e1;
  border-left: 4px solid #e67e22;
  color: #7a4a00;
}

.status-done {
  background: #eaf7ef;
  border-left: 4px solid #27ae60;
  color: #1a6b3c;
}

.pill-big {
  padding: 6px 14px;
  font-size: 13px;
}

.pending-row {
  cursor: pointer;
  color: #9a6a2c;
}

.pending-row:hover {
  background: #fff8e1;
}

/* the title on the left and its buttons on the right, on one line */
.section-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* the orders list scrolls on its own; its heading row stays in view */
.orders-scroll {
  max-height: 420px;
  overflow-y: auto;
  border: 1px solid #e6e8f0;
}

.orders-scroll .pallet-orders {
  width: 100%;
  margin-bottom: 0;
  border: 0;
}

.orders-scroll .pallet-orders thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #f7f8fc;
  box-shadow: 0 1px 0 #e6e8f0;
}

.pallet-orders th,
.pallet-orders td {
  padding: 8px 10px;
}

.add-box {
  margin-bottom: 14px;
  padding: 12px 14px;
  background: #f7f8fc;
  border: 1px solid #e6e8f0;
  border-radius: 6px;
}

.add-box label {
  display: block;
  margin-bottom: 6px;
  font-weight: normal;
  color: #6b7285;
}

.add-box textarea.form-control {
  margin-bottom: 8px;
  padding: 6px 10px;
  background: #fff;
  border: 1px solid #d5d9e3;
  border-radius: 4px;
}

.invoice-money {
  margin-left: 10px;
  font-weight: 600;
}

.income-table td {
  vertical-align: middle;
}

.income-table td small {
  display: block;
  margin-top: 2px;
  color: #9aa0b0;
  font-size: 12px;
}

.income-table .income-total td {
  font-weight: bold;
}
</style>



// WEBPACK FOOTER //
// src/components/pallet/pallets.vue